import { ref } from "vue";
import type { AdminCoach } from "@/api/model";
import {
  adminV1OrganizationsCoachesCreate,
  adminV1OrganizationsCoachesDestroy,
} from "@/api/admin-coaches/admin-coaches";

export function useCoachManagement() {
  const isCreating = ref(false);
  const isDeleting = ref(false);
  const error = ref<string | null>(null);

  async function makeCoach(
    organizationId: number,
    profileId: number
  ): Promise<{ success: boolean; coachId?: number }> {
    isCreating.value = true;
    error.value = null;

    try {
      const result = await adminV1OrganizationsCoachesCreate(organizationId, {
        profile_id: profileId,
      });

      // Check if the API returned an error
      if (result && typeof result === "object" && "error" in result) {
        error.value = (result as any).error || "Failed to create staff member";
        console.error("Error response:", result);
        return { success: false };
      }

      // Success - result is the coach data with ID
      const coachData = result as AdminCoach;
      return { success: true, coachId: coachData.id };
    } catch (err) {
      error.value = "An error occurred while creating staff member";
      console.error("Error creating coach:", err);
      return { success: false };
    } finally {
      isCreating.value = false;
    }
  }

  async function removeCoach(
    organizationId: number,
    coachId: number
  ): Promise<boolean> {
    isDeleting.value = true;
    error.value = null;

    try {
      await adminV1OrganizationsCoachesDestroy(organizationId, coachId);

      // If we get here without an exception, the delete was successful
      return true;
    } catch (err: any) {
      // If it's a 404, the coach is already deleted - treat as success
      if (err?.status === 404) {
        console.log("Coach already deleted (404), treating as success");
        return true;
      }

      error.value =
        err?.message || "An error occurred while removing staff member";
      console.error("Error removing coach:", err);
      return false;
    } finally {
      isDeleting.value = false;
    }
  }

  return {
    isCreating,
    isDeleting,
    error,
    makeCoach,
    removeCoach,
  };
}
